import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { motion } from "framer-motion";
import { Menu, X } from "lucide-react";
import Logo from "@/components/Logo";

const NAV = [
  { label: "Why Liver Health", href: "#why" },
  { label: "Meet Our Team", href: "#founders" },
  { label: "Program", href: "#roadmap" },
  // ORYVEN tab hidden for now (reactivate later): { label: "ORYVEN", href: "/oryven" },
  // Products tab hidden with the PrecisionNutrition section — restore both together
  // { label: "Products", href: "#precision-nutrition" },
  { label: "Impact", href: "#impact" },
  { label: "Contact", href: "#contact" },
];

const scrollTo = (href, navigate) => {
  if (href.startsWith("/")) { navigate(href); return; }
  const el = document.querySelector(href);
  if (!el) return;
  if (window.__lenis) window.__lenis.scrollTo(el, { offset: -80 });
  else el.scrollIntoView({ behavior: "smooth" });
};

export const Header = () => {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll, { passive: true });
    onScroll();
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      initial={{ y: -80, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1], delay: 0.2 }}
      className={`fixed inset-x-0 top-0 z-50 transition-[background-color,backdrop-filter,border-color] duration-500 ${
        scrolled
          ? "bg-sa-bg/80 backdrop-blur-xl border-b border-sa-beige"
          : "bg-transparent border-b border-transparent"
      }`}
      data-testid="site-header"
    >
      <div className="mx-auto flex max-w-7xl items-center justify-between px-5 py-4 lg:px-6 xl:px-12">
        <button
          onClick={() => window.scrollTo({ top: 0, behavior: "smooth" })}
          className="flex shrink-0 items-center"
          data-testid="logo-home"
        >
          <Logo iconClass="h-9 w-9 md:h-10 md:w-10" textClass="text-xl md:text-2xl" />
        </button>

        <nav className="ml-5 hidden items-center gap-4 lg:flex xl:ml-8 xl:gap-5">
          {NAV.map((n) => (
            <button
              key={n.href}
              onClick={() => scrollTo(n.href, navigate)}
              className="whitespace-nowrap text-[12.5px] font-medium text-sa-muted transition-colors duration-300 hover:text-sa-forest xl:text-[14px]"
              data-testid={`nav-${n.href.replace(/^[#/]/, "")}`}
            >
              {n.label}
            </button>
          ))}
        </nav>

        <div className="ml-5 flex items-center gap-2 xl:ml-8 xl:gap-2.5">
          <button
            onClick={() => navigate("/assessment")}
            className="hidden whitespace-nowrap rounded-full bg-sa-forest px-3.5 py-2.5 text-[12.5px] font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.03] hover:bg-sa-forest-soft sm:inline-flex xl:px-5 xl:text-[14px]"
            data-testid="header-start-assessment"
          >
            Start Free Assessment
          </button>
          <button
            onClick={() => navigate("/book")}
            className="hidden whitespace-nowrap rounded-full bg-sa-forest px-3.5 py-2.5 text-[12.5px] font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.03] hover:bg-sa-forest-soft xl:inline-flex xl:px-5 xl:text-[14px]"
            data-testid="header-book-appointment"
          >
            Book Evaluation
          </button>
          <button
            onClick={() => navigate("/consultation")}
            className="hidden whitespace-nowrap rounded-full bg-sa-copper px-3.5 py-2.5 text-[12.5px] font-medium text-white transition-[transform,background-color] duration-300 hover:scale-[1.03] hover:bg-sa-copper-deep 2xl:inline-flex 2xl:px-5 2xl:text-[14px]"
            data-testid="header-online-consultation"
          >
            Online Consultation
          </button>
          <button
            className="lg:hidden text-sa-forest"
            onClick={() => setOpen((v) => !v)}
            data-testid="mobile-menu-toggle"
            aria-label="Menu"
          >
            {open ? <X className="h-6 w-6" /> : <Menu className="h-6 w-6" />}
          </button>
        </div>
      </div>

      {open && (
        <motion.div
          initial={{ opacity: 0, height: 0 }}
          animate={{ opacity: 1, height: "auto" }}
          className="lg:hidden overflow-hidden border-t border-sa-beige bg-sa-bg/95 backdrop-blur-xl"
        >
          <div className="flex flex-col gap-1 px-6 py-4">
            {NAV.map((n) => (
              <button
                key={n.href}
                onClick={() => {
                  scrollTo(n.href, navigate);
                  setOpen(false);
                }}
                className="py-2 text-left text-[15px] font-medium text-sa-forest"
                data-testid={`mobile-nav-${n.href.replace(/^[#/]/, "")}`}
              >
                {n.label}
              </button>
            ))}
            <div className="mt-3 grid grid-cols-2 gap-3">
              <button
                onClick={() => {
                  navigate("/book");
                  setOpen(false);
                }}
                className="rounded-full border border-sa-forest/25 px-4 py-3 text-center text-[14px] font-medium text-sa-forest"
                data-testid="mobile-book-appointment"
              >
                Book Evaluation
              </button>
              <button
                onClick={() => {
                  navigate("/consultation");
                  setOpen(false);
                }}
                className="rounded-full bg-sa-copper px-4 py-3 text-center text-[14px] font-medium text-white"
                data-testid="mobile-online-consultation"
              >
                Online Consultation
              </button>
              <button
                onClick={() => navigate("/assessment")}
                className="col-span-2 rounded-full bg-sa-forest px-4 py-3 text-center text-[14px] font-medium text-white"
                data-testid="mobile-start-assessment"
              >
                Start Free Assessment
              </button>
            </div>
          </div>
        </motion.div>
      )}
    </motion.header>
  );
};

export default Header;
